import React, {ReactElement, useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import INCaseLogo from '../../../../../../assets/images/ui/incase-logo-black.svg';
import {DeviceType} from '../../../../../incase-domain/common/DeviceType.enum';
import {IElementImageMarkup, IPoint, IShape} from '../../../../../incase-domain/elements/question/imageMarkup';
import {ElementInteraction, MarkupImageElementInteractions} from '../../../../../incase-domain/proptypes/runfile';
import {loadedPresentationSelector} from '../../../../../store/selectors/presentation.selectors';
import {getBorderStyle} from '../../../../../util/style.utils';
import QuestionLayout from '../common/QuestionLayout';
import {handleSelectAnswers} from '../utils/handleTryButton.question.utils';
import ImageMarkupClickCanvas from './ImageMarkupClickCanvas';
import {useImageMarkupContext} from './ImageMarkupContext';
import ImageMarkupFreehandCanvas from './ImageMarkupFreehandCanvas';
import useImageMarkupTry from './useImageMarkupTry';

interface ImageMarkupQuestionElementProps {
    element: IElementImageMarkup;
    savePresentationRunFile: (elementRunFile: ElementInteraction, newPoints?: number) => void;
    elementInteractions: MarkupImageElementInteractions;
    device?: DeviceType;
}

function ImageMarkupQuestionElement(props: ImageMarkupQuestionElementProps): ReactElement {
    const {element, savePresentationRunFile, elementInteractions, device} = props;
    const {imageSrc, title, markedAreas, borderRadius} = element;

    const handleTry = useImageMarkupTry();
    const {refsMap, addRef} = useImageMarkupContext();

    const presentation = useSelector(loadedPresentationSelector);
    const {answered} = elementInteractions;

    const imageRef = useRef<HTMLImageElement | null>(null);
    const canvasesRef = useRef<Map<number, HTMLCanvasElement> | null>(null);

    const [canvasDimensions, setCanvasDimensions] = useState<{width: number; height: number}>({width: 0, height: 0});

    const updateCanvasDimensions = useCallback((): void => {
        if (imageRef.current) {
            const {clientWidth: width, clientHeight: height} = imageRef.current;
            setCanvasDimensions((prev) => (prev.width === width && prev.height === height ? prev : {width, height}));
        }
    }, []);

    useEffect(() => {
        const img = imageRef.current;
        if (!img)
            return () => {
                // noop
            };

        updateCanvasDimensions();

        const resizeObserver = new ResizeObserver(updateCanvasDimensions);
        resizeObserver.observe(img);

        return () => {
            resizeObserver.disconnect();
        };
    }, [updateCanvasDimensions]);

    const [localMarkedShapes, setLocalMarkedShapes] = useState<IShape[]>(
        (elementInteractions.answered ? elementInteractions.answers : elementInteractions.markedAnswers) || []
    );

    // Sync local state with elementInteractions when it changes externally
    useEffect(() => {
        if (answered) {
            const externalShapes = elementInteractions.answers || [];
            setLocalMarkedShapes(externalShapes);
        } else {
            const externalShapes = elementInteractions.markedAnswers || [];
            setLocalMarkedShapes(externalShapes);
        }
    }, [elementInteractions.answered, elementInteractions.answers, elementInteractions.markedAnswers, answered]);

    const userMarkedShapes = answered ? elementInteractions.answers || [] : localMarkedShapes;

    useEffect(() => {
        if (refsMap[element.id]) return;

        addRef(element.id, {imageRef, canvasesRef});
    }, [element.id, imageRef, canvasesRef, addRef, refsMap]);

    const getCanvasesMap = (): Map<number, HTMLCanvasElement> => {
        if (!canvasesRef.current) {
            /* eslint-disable no-param-reassign */
            canvasesRef.current = new Map();
        }
        return canvasesRef.current;
    };

    useLayoutEffect(() => {
        const map = getCanvasesMap();
        if (canvasesRef.current?.size && canvasesRef.current?.size > 0 && markedAreas?.length > 0) {
            markedAreas.forEach((shape: IShape, index) => {
                const canvas = map.get(index);
                const ctx = canvas?.getContext('2d');
                if (canvas && ctx) {
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                    ctx.beginPath();
                    ctx.moveTo(shape.points[0].x * canvas.width, shape.points[0].y * canvas.height);
                    shape.points.forEach((point: IPoint) => {
                        ctx.lineTo(point.x * canvas.width, point.y * canvas.height);
                    });
                    ctx.fillStyle = 'rgba(255, 0, 0, 0.2)';
                    ctx.fill();
                    ctx.strokeStyle = 'purple';
                    ctx.lineWidth = 2;
                    ctx.stroke();
                }
            });
        }
    }, [markedAreas, elementInteractions, canvasesRef.current?.size, canvasDimensions]); // eslint-disable-line react-hooks/exhaustive-deps

    function handleSelectUpdate(shapes: IShape[]): void {
        setLocalMarkedShapes(shapes);
    }

    function handleSelectSave(shapes: IShape[]): void {
        setLocalMarkedShapes(shapes);
        handleSelectAnswers(elementInteractions, element, shapes, savePresentationRunFile, presentation);
    }

    if (!imageSrc) return <></>;

    return (
        <QuestionLayout
            element={element}
            elementInteractions={elementInteractions}
            device={device}
            showConfirmButton
            onConfirm={() => {
                const tempElementInteractions = {
                    ...elementInteractions,
                    markedAnswers: localMarkedShapes,
                };
                handleTry(element, tempElementInteractions);
            }}
            confirmDisabled={elementInteractions.answered || localMarkedShapes.length === 0}
        >
            <div className={`c-imageMarkup ${device === DeviceType.MOBILE ? 'c-imageMarkup--mobile' : ''}`}>
                <div className="c-imageMarkup__canvas">
                    <img
                        ref={imageRef}
                        src={imageSrc || INCaseLogo}
                        alt=""
                        title={title}
                        onLoad={updateCanvasDimensions}
                        style={{
                            borderRadius: getBorderStyle(borderRadius),
                        }}
                    />
                    {markedAreas?.map((area, index) => (
                        <canvas
                            key={`result-canvas-${index}`}
                            className="c-imageMarkup__canvas"
                            width={canvasDimensions.width}
                            height={canvasDimensions.height}
                            ref={(node) => {
                                const map = getCanvasesMap();
                                if (node) {
                                    map.set(index, node);
                                } else {
                                    map.delete(index);
                                }
                            }}
                            style={{visibility: answered ? 'visible' : 'hidden'}}
                        />
                    ))}
                    {element.intersectionAnswerType ? (
                        <ImageMarkupFreehandCanvas
                            width={canvasDimensions.width}
                            height={canvasDimensions.height}
                            onShapeChange={handleSelectUpdate}
                            onShapeSave={handleSelectSave}
                            shapes={userMarkedShapes}
                            answered={elementInteractions.answered}
                            isDebatable={element.isDebatable}
                        />
                    ) : (
                        <ImageMarkupClickCanvas
                            width={canvasDimensions.width}
                            height={canvasDimensions.height}
                            onClick={handleSelectSave}
                            clickAnswers={userMarkedShapes}
                            answered={elementInteractions.answered}
                            isDebatable={element.isDebatable}
                        />
                    )}
                </div>
            </div>
        </QuestionLayout>
    );
}

export default ImageMarkupQuestionElement;
